import { useEffect, useRef, useState } from "react";

export type LatLongResult = {
  latitude: number;
  longitude: number;
  loading: boolean;
  error: GeolocationPositionError | null;
};

export const useLatLong = () => {
  const [currentPosition, setCurrentPosition] = useState<LatLongResult>({
    latitude: 0,
    longitude: 0,
    loading: true,
    error: null,
  });
  const hasAttempted = useRef<boolean>(false);

  const updatePosition = () => {
    if (hasAttempted.current) {
      return; // only try once per page load
    }
    
    hasAttempted.current = true;
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (res: GeolocationPosition) => {
          setCurrentPosition({
            latitude: res.coords.latitude,
            longitude: res.coords.longitude,
            loading: false,
            error: null,
          });
        },
        (err: GeolocationPositionError) => {
          setCurrentPosition({
            latitude: 0,
            longitude: 0,
            loading: false,
            error: err,
          });
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        }
      );
    } else {
      setCurrentPosition({
        latitude: 0,
        longitude: 0,
        loading: false,
        error: { code: 0, message: 'Geolocation not supported' } as GeolocationPositionError,
      });
    }
  };

  const getCurrentLocation = (): Promise<{ latitude: number; longitude: number } | null> => {
    return new Promise((resolve) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (res: GeolocationPosition) => {
            resolve({
              latitude: res.coords.latitude,
              longitude: res.coords.longitude,
            });
          },
          (err: GeolocationPositionError) => {
            resolve(null);
          },
          {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0, // Always get fresh location
          }
        );
      } else {
        resolve(null);
      }
    });
  };

  useEffect(() => {
    updatePosition(); // Try to get position once on mount
  }, []);

  return { ...currentPosition, getCurrentLocation };
};
